/**
 * "The Recall Test" — content and scoring for the /challenge game.
 *
 * The game exists to dramatise one tradeoff honestly: capturing fast and
 * capturing findably pull against each other. Everything here is deliberately
 * static and deterministic — no shuffling, no wall-clock reads at module load
 * — because the page is prerendered and then hydrated, and a value that
 * differs between those two renders is a hydration mismatch (see the
 * PUBLISH_CUTOFF note in vite.config.ts for the version of this bug we already
 * shipped once).
 */

export type ZoneId = "decisions" | "owners" | "dates" | "risks";

/** Where a captured fragment ended up. "dump" is the unstructured pile. */
export type Placement = ZoneId | "dump";

export interface Zone {
  id: ZoneId;
  label: string;
  /** Shown under the label so the choice is quick to make under time pressure. */
  hint: string;
  /** Keyboard shortcut during the capture round. */
  key: string;
}

export const ZONES: Zone[] = [
  { id: "decisions", label: "Decisions", hint: "what we agreed", key: "1" },
  { id: "owners", label: "Owners", hint: "who has it", key: "2" },
  { id: "dates", label: "Dates", hint: "when", key: "3" },
  { id: "risks", label: "Risks", hint: "what could bite us", key: "4" },
];

export interface Fragment {
  id: number;
  text: string;
}

/**
 * A single realistic kickoff meeting, in the order the lines would land. Some
 * fragments are the answer to a recall question, and some exist only to be a
 * plausible near-miss for one — a wall of notes is only hard to search when
 * the wrong lines look like the right one.
 */
export const FRAGMENTS: Fragment[] = [
  { id: 1, text: "Budget approved at $48,000 — not the $60,000 in the proposal" },
  { id: 2, text: "Priya owns the migration workstream" },
  { id: 3, text: "Launch slips to Nov 14 — the 7th was never realistic" },
  { id: 4, text: "Legal hasn't cleared the new name yet" },
  { id: 5, text: "Dropping the mobile app from phase one" },
  { id: 6, text: "Dan is the only one with CMS access, and he's out from the 20th" },
  { id: 7, text: "Client wants weekly check-ins, Thursdays at 9am" },
  { id: 8, text: "Marcus signs off on all creative" },
  { id: 9, text: "Content freeze starts Oct 30" },
  { id: 10, text: "Their brand guidelines are from 2019 and nobody can find the source files" },
  { id: 11, text: "Agreed: no scope changes after the freeze" },
  { id: 12, text: "Sam takes over QA from the previous agency" },
  { id: 13, text: "Phase two budget is a separate conversation in January" },
  { id: 14, text: "If legal hasn't cleared the name by Nov 1, launch moves to December" },
  { id: 15, text: "Their CFO wants a cost breakdown before the 15th" },
  { id: 16, text: "Reusing the existing design system — no new component library" },
];

export interface Question {
  id: number;
  prompt: string;
  options: string[];
  /** Index into `options`. */
  answer: number;
  /** The fragment the answer is in — asserted against by the tests. */
  sourceFragmentId: number;
}

export const QUESTIONS: Question[] = [
  {
    id: 1,
    prompt: "What budget was approved?",
    options: ["$48,000", "$60,000", "$84,000", "It wasn't decided"],
    answer: 0,
    sourceFragmentId: 1,
  },
  {
    id: 2,
    prompt: "Who owns the migration workstream?",
    options: ["Marcus", "Priya", "Sam", "Dan"],
    answer: 1,
    sourceFragmentId: 2,
  },
  {
    id: 3,
    prompt: "When does the content freeze start?",
    options: ["Nov 14", "Nov 1", "Oct 30", "Oct 20"],
    answer: 2,
    sourceFragmentId: 9,
  },
  {
    id: 4,
    prompt: "What happens if legal hasn't cleared the name by Nov 1?",
    options: [
      "The launch slips to Nov 14",
      "Launch moves to December",
      "Phase two is cancelled",
      "No scope changes after the freeze",
    ],
    answer: 1,
    sourceFragmentId: 14,
  },
  {
    id: 5,
    prompt: "What got cut from phase one?",
    options: ["The design system work", "QA", "The mobile app", "The weekly check-ins"],
    answer: 2,
    sourceFragmentId: 5,
  },
];

/** How long a single fragment stays on screen before it is lost. */
export const FRAGMENT_MS = 2800;
/** The whole recall round shares one clock, so a fast answer buys time later. */
export const RECALL_MS = 25000;

export interface CaptureRecord {
  fragmentId: number;
  placement: Placement;
}

export interface Score {
  captured: number;
  total: number;
  placed: number;
  dumped: number;
  recalled: number;
  questions: number;
}

export function scoreRun(
  records: CaptureRecord[],
  answers: (number | null)[]
): Score {
  const placed = records.filter((r) => r.placement !== "dump").length;
  return {
    captured: records.length,
    total: FRAGMENTS.length,
    placed,
    dumped: records.length - placed,
    recalled: QUESTIONS.reduce(
      (n, q, i) => (answers[i] === q.answer ? n + 1 : n),
      0
    ),
    questions: QUESTIONS.length,
  };
}

export interface Verdict {
  headline: string;
  body: string;
}

/**
 * The result screen's argument. Both rates are read against the same
 * thresholds so the four quadrants are the four honest outcomes — including
 * the one where the player beats the tradeoff, which is reachable and should
 * be said plainly rather than scolded into a sales line.
 */
export function verdictFor(score: Score): Verdict {
  const captureRate = score.total === 0 ? 0 : score.captured / score.total;
  const recallRate = score.questions === 0 ? 0 : score.recalled / score.questions;
  const caught = captureRate >= 0.75;
  const found = recallRate >= 0.6;

  if (caught && found) {
    return {
      headline: "You beat the tradeoff.",
      body: "You kept up with the room and you can still answer for it. This is the rare one — and it took deciding where each line went while the meeting was still moving.",
    };
  }
  if (caught && !found) {
    return {
      headline: "You caught it all. You can't find any of it.",
      body: "This is the common ending, and it doesn't feel like failure at the time — the notes exist, the meeting felt captured. The bill arrives later, when someone asks what the budget was.",
    };
  }
  if (!caught && found) {
    return {
      headline: "Immaculate notes. Half the meeting is missing.",
      body: "Everything you kept, you can find. You just spent the room's time filing instead of listening, and the lines you dropped are gone for good.",
    };
  }
  return {
    headline: "The meeting won.",
    body: "Too fast to file, too scattered to search. Most people land here the first time — the round is deliberately quicker than a real room, but the shape of the problem is the same one.",
  };
}

/** The line a player copies to share their result. */
export function shareLine(score: Score): string {
  return (
    `I captured ${score.captured}/${score.total} of the meeting ` +
    `and could find ${score.recalled}/${score.questions} of it afterwards. ` +
    `The Recall Test — omnicanvasnotes.com/challenge`
  );
}
